import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { TarjetaInforme } from '#components/mobile/informes/comunes';
import type { Tono } from '#components/mobile/informes/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { color } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

import { LineaRitmo } from './graficas';
import { acumuladoPorDia, estadoRitmo } from './ritmo';
import type { EstadoRitmo } from './ritmo';
import { useGastoDelMes } from './useGastoDelMes';
import { useGastoDiario } from './useGastoDiario';
import { useSaldosMes } from './useSaldosMes';

const TONO: Record<EstadoRitmo, Tono> = {
  bien: 'bien',
  justo: 'aviso',
  pasa: 'mal',
};

/** Etiqueta «Vas bien / Vas justo / Te pasas». */
export function ChipEstado({ estado }: { estado: EstadoRitmo }) {
  const { t } = useTranslation();
  const tonos = {
    bien: { fg: color.ok, bg: color.okSoft, texto: t('You are on track') },
    justo: {
      fg: color.warn,
      bg: color.warnSoft,
      texto: t('You are cutting it close'),
    },
    pasa: {
      fg: color.bad,
      bg: color.badSoft,
      texto: t('You are overspending'),
    },
  }[estado];
  return (
    <View
      data-testid="chip-ritmo"
      style={{
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        borderRadius: 999,
        padding: '3px 10px',
        backgroundColor: tonos.bg,
      }}
    >
      <View
        style={{
          width: 7,
          height: 7,
          borderRadius: 4,
          backgroundColor: tonos.fg,
        }}
      />
      <Text style={{ fontSize: 12, fontWeight: 800, color: tonos.fg }}>
        {tonos.texto}
      </Text>
    </View>
  );
}

/**
 * «Cómo va el mes»: lo que te queda del plan y si vas bien. Pequeña = lo que
 * queda y la etiqueta; ancha = + la frase; grande = + el ritmo día a día.
 */
export function TarjetaRitmo({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const {
    mes,
    dia,
    diasMes,
    categorias,
    excluidas,
    desglose,
    analisis,
    isLoading,
  } = useGastoDelMes();
  const { saldos, isLoading: cargandoSaldos } = useSaldosMes(mes);
  const { porDia } = useGastoDiario({
    mes,
    categorias,
    excluidas,
    activo: tamano === 'grande',
  });

  let plan = 0;
  for (const [id, celdas] of saldos ?? []) {
    const info = categorias.get(id);
    if (info && !info.esIngreso && !info.oculta && !excluidas.has(id)) {
      plan += celdas.asignado;
    }
  }
  const gastado = desglose.total;
  const fijos = analisis.filas
    .filter(f => f.esFijo)
    .reduce((s, f) => s + f.importe, 0);
  // Sin nada asignado, la referencia es lo habitual del mes.
  const esperadoHoy = analisis.filas.reduce(
    (s, f) => s + (f.esperado ?? f.importe),
    0,
  );
  const conPlan = plan > 0;
  const referencia = conPlan
    ? plan
    : Math.round((esperadoHoy * diasMes) / Math.max(dia, 1));
  const r = estadoRitmo({
    gastado,
    referencia,
    fijos: conPlan ? fijos : 0,
    dia,
    diasMes,
  });
  const cargando = isLoading || cargandoSaldos;
  const nombreMes = monthUtils.format(mes, 'MMMM', locale);

  const valor = cargando
    ? '…'
    : !conPlan
      ? fmt(gastado)
      : r.queda >= 0
        ? t('{{amount}} left', { amount: fmt(r.queda) })
        : t('{{amount}} over', { amount: fmt(-r.queda) });
  const frase = cargando
    ? ''
    : tamano === 'compacto'
      ? ''
      : conPlan
        ? t('You have spent {{spent}} of {{plan}}. Day {{day}} of {{days}}.', {
            spent: fmt(gastado),
            plan: fmt(plan),
            day: dia,
            days: diasMes,
          })
        : t(
            'Nothing assigned this month: compared with your usual {{usual}} by today.',
            { usual: fmt(esperadoHoy) },
          );

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-ritmo"
      titulo={
        tamano === 'compacto'
          ? nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1)
          : t('How {{month}} is going', { month: nombreMes })
      }
      valor={valor}
      frase={frase}
      tono={cargando ? 'neutro' : TONO[r.estado]}
      onPress={() => void navigate('/reports/gasto')}
    >
      {!cargando && (
        <View style={{ gap: 8 }}>
          <ChipEstado estado={r.estado} />
          {tamano === 'grande' && porDia && referencia > 0 && (
            <>
              <LineaRitmo
                acumulado={acumuladoPorDia(porDia, mes, dia)}
                diasMes={diasMes}
                referencia={referencia}
                fijos={conPlan ? fijos : 0}
                etiquetaInicio={monthUtils.format(`${mes}-01`, 'd MMM', locale)}
                etiquetaHoy={t('today')}
              />
              <Text style={{ fontSize: 12, fontWeight: 600, color: color.fg3 }}>
                {conPlan
                  ? t(
                      'Line: your spending. Dots: the pace to end the month on plan.',
                    )
                  : t('Line: your spending. Dots: your usual pace.')}
              </Text>
            </>
          )}
        </View>
      )}
    </TarjetaInforme>
  );
}
